import Vue from 'vue';

// Shirt colours of the competitors, by team id, collected from the API
// responses (event-detail, popular, live, ...) so every LogoCompetitors can draw
// a coloured shirt even when it only receives the team id.
const state = Vue.observable({ byTeamId: {} });

// '#abc', '#aabbcc', 'aabbcc' -> '#aabbcc'; anything else -> null.
export const normalizeHex = value => {
  if (value === null || value === undefined) return null;
  let v = String(value).trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(v)) v = v.split('').map(c => c + c).join('');
  return /^[0-9a-f]{6}$/i.test(v) ? `#${v.toLowerCase()}` : null;
};

// Only the three shirt colours of a competitor are kept (frozen, so Vue doesn't
// make them reactive), or null when it has no primary colour.
const extractShirtColors = competitor => {
  const primary = normalizeHex(competitor.shirtColorPrimary);
  if (!primary) return null;
  return Object.freeze({
    primary,
    number: normalizeHex(competitor.shirtColorNumber),
    outline: normalizeHex(competitor.shirtColorOutline),
  });
};

export const registerCompetitors = competitors => {
  if (!Array.isArray(competitors)) return;
  competitors.forEach(c => {
    if (!c || c.id === undefined || c.id === null) return;
    const colors = extractShirtColors(c);
    if (!colors) return;
    const key = String(c.id);
    const prev = state.byTeamId[key];
    if (prev && prev.primary === colors.primary && prev.number === colors.number && prev.outline === colors.outline) return;
    Vue.set(state.byTeamId, key, colors);
  });
};

export const getShirtColors = teamId =>
  (teamId === undefined || teamId === null) ? null : state.byTeamId[String(teamId)] || null;

// Walk limits for registerCompetitorsDeep: responses are trusted JSON, the caps
// only bound the cost on unusually large payloads.
const MAX_DEPTH = 12;
const MAX_NODES = 100000;

// Registers every `competitors` array found anywhere in an API response, so the
// shirt colours reach the logos of every widget (popular, live, next events...)
// without each component having to pass them down.
export const registerCompetitorsDeep = data => {
  if (!data || typeof data !== 'object') return;
  const stack = [[data, 0]];
  let visited = 0;
  while (stack.length && visited < MAX_NODES) {
    const [node, depth] = stack.pop();
    visited++;
    if (Array.isArray(node)) {
      if (depth < MAX_DEPTH) node.forEach(item => { if (item && typeof item === 'object') stack.push([item, depth + 1]); });
      continue;
    }
    Object.keys(node).forEach(key => {
      const value = node[key];
      if (!value || typeof value !== 'object') return;
      if (key === 'competitors' && Array.isArray(value)) {
        registerCompetitors(value);
        return;
      }
      if (depth < MAX_DEPTH) stack.push([value, depth + 1]);
    });
  }
};
